import React from 'react';
import styled from 'styled-components';
import { FaCheck, FaTimes } from 'react-icons/fa';

const ComparisonSection = styled.section`
  padding: 8rem 2rem 4rem 2rem;
  background: ${props => props.theme.background};
  text-align: center;
  flex: 1;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 640px) {
    padding: 6rem 1rem 2.5rem 1rem;
  }
`;

const ComparisonTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 3rem;
  color: ${props => props.theme.textPrimary};

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    margin-bottom: 1.75rem;
  }
`;

const ComparisonTable = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px ${props => props.theme.shadow || 'rgba(0, 0, 0, 0.05)'};
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.cardBackground};

  @media (max-width: 1024px) {
    overflow-x: auto;
    min-width: 800px;
  }

  @media (max-width: 640px) {
    border-radius: 12px;
    min-width: 760px;
  }
`;

const TableRow = styled.div`
  display: contents;
  
  &:last-child div {
    border-bottom: none;
  }
`;

const TableCell = styled.div`
  padding: 1.5rem;
  background: ${props => props.$highlight ? `${props.theme.primary}08` : 'transparent'};
  border-bottom: 1px solid ${props => props.theme.border};
  border-right: 1px solid ${props => props.theme.border};
  display: flex;
  align-items: center;
  justify-content: ${props => props.$center ? 'center' : 'flex-start'};
  color: ${props => props.theme.textPrimary};
  font-weight: ${props => props.$header ? '700' : '500'};
  font-size: ${props => props.$header ? '1.1rem' : '1rem'};
  line-height: 1.4;
  min-width: 0;
  word-break: break-word;

  svg {
    font-size: 1.25rem;
  }

  &:nth-child(5n) {
    border-right: none;
  }

  @media (max-width: 640px) {
    padding: 0.9rem 0.5rem;
    font-size: ${props => props.$header ? '0.8rem' : '0.78rem'};

    svg {
      font-size: 1rem;
    }
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0.4rem;
    font-size: ${props => props.$header ? '0.72rem' : '0.7rem'};
  }
`;

const CheckMark = styled(FaCheck)`
  color: ${props => props.theme.buttonSuccess || '#28a745'};
`;

const CrossMark = styled(FaTimes)`
  color: ${props => props.theme.buttonDanger || '#dc3545'};
  opacity: 0.5;
`;

const Features = () => {
  return (
    <ComparisonSection>
      <ComparisonTitle>Compare PlanMySemester with Other Tools</ComparisonTitle>
      <ComparisonTable>
        <TableRow>
          <TableCell $header>Feature / Tool</TableCell>
          <TableCell $header $center $highlight>PlanMySemester</TableCell>
          <TableCell $header $center>MyStudyLife</TableCell>
          <TableCell $header $center>Coursicle</TableCell>
          <TableCell $header $center>Notion</TableCell>
        </TableRow>
        
        {/* === CATEGORY 1: UNIQUE VALUE PROPOSITIONS (The "Why Buy" Row) === */}
        {/* Immediate visual hook. No one else natively builds flashcards into the workflow. */}
        <TableRow>
          <TableCell>Flashcards</TableCell>
          <TableCell $center $highlight><CheckMark /></TableCell>
          <TableCell $center><CrossMark /></TableCell>
          <TableCell $center><CrossMark /></TableCell>
          <TableCell $center><CrossMark /></TableCell>
        </TableRow>

        {/* === CATEGORY 2: THE NOTION KILLERS (Frictionless Power) === */}
        {/* Highlights that you match Notion's flexibility but bypass its legendary setup headaches */}
        <TableRow>
          <TableCell>Ease of Setup</TableCell>
          <TableCell $center $highlight><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
          <TableCell $center><CrossMark /></TableCell>
        </TableRow>

        <TableRow>
          <TableCell>Assignment Management</TableCell>
          <TableCell $center $highlight><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
          <TableCell $center><CrossMark /></TableCell>
        </TableRow>

        {/* === CATEGORY 3: ADVANCED STUDY FEATURES === */}
        {/* Knocks out the narrow schedule-builders (MyStudyLife & Coursicle) */}
        <TableRow>
          <TableCell>Flashcard Imports</TableCell>
          <TableCell $center $highlight><CheckMark /></TableCell>
          <TableCell $center><CrossMark /></TableCell>
          <TableCell $center><CrossMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
        </TableRow>

        <TableRow>
          <TableCell>Note-taking</TableCell>
          <TableCell $center $highlight><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
          <TableCell $center><CrossMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
        </TableRow>

        <TableRow>
          <TableCell>File Uploads</TableCell>
          <TableCell $center $highlight><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
          <TableCell $center><CrossMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
        </TableRow>

        <TableRow>
          <TableCell>Built-in Study Tracker</TableCell>
          <TableCell $center $highlight><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
          <TableCell $center><CrossMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
        </TableRow>

        <TableRow>
          <TableCell>Analytics & Progress Tracking</TableCell>
          <TableCell $center $highlight><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
          <TableCell $center><CrossMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
        </TableRow>

        {/* === CATEGORY 4: TABLE STAKES (Baseline Features) === */}
        {/* Necessary features, but they provide zero competitive advantage. Keep at the bottom. */}
        <TableRow>
          <TableCell>Customization</TableCell>
          <TableCell $center $highlight><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
        </TableRow>

        <TableRow>
          <TableCell>Smart Calendar & Scheduling</TableCell>
          <TableCell $center $highlight><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
        </TableRow>

        <TableRow>
          <TableCell>Mobile & Responsive UI</TableCell>
          <TableCell $center $highlight><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
        </TableRow>

        <TableRow>
          <TableCell>Reminders & Notifications</TableCell>
          <TableCell $center $highlight><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
          <TableCell $center><CheckMark /></TableCell>
        </TableRow>
      </ComparisonTable>
    </ComparisonSection>
  );
};

export default Features;
